import React, { useEffect, useState, Fragment } from "react";
import { Card, Table, Checkbox, Popover, Divider, Row, Col, Tooltip, Button, Space } from "antd";
import styles from "../topology.module.scss";
import RouterStateFilterC from "./RouterStateFilterC";
import { SettingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/hook";
import exportToExcel from "./exportToExcel";
import type { ColumnsType } from "antd/lib/table";
import AutoRefresh from "Components/Shared/AutoRefresh";
import Refresh from "Components/Shared/Refresh";
import { useDeviceStatus } from "Components/Dashboard/queries";
import { useIdentity } from "../queries";
import { Uptime } from "Utility/Fetch";

const CheckboxGroup = Checkbox.Group;

const ExtraOptions = ["Device", "Location", "Model", "Health", "Strength", "Description"];

const ExtraColumns = ExtraOptions.map((item) => {
  const column_width = item === "Model" || item === "Health" || item === "Strength" ? 100 : 170;
  return {
    width: column_width,
    title: item,
    dataIndex: item,
    sorter: (a: any, b: any) => {
      return a[item]?.length || 0 - b[item]?.length || 0;
    },
    sortDirections: ["descend", "ascend"],
    fixed: item === "Device" ? "left" : null,
  };
});

const statusOptions = {
  Identity: [
    "IMEI",
    "Bootloader Version",
    "Hardware MCSV",
    "Hostname",
    "LAN Ethernet MAC",
    "Modem Firmware Version",
    "Serial Number",
    "WAN Ethernet MAC",
    "WiFi AP MAC",
    "Dual Image A MCSV",
    "Dual Image B MCSV",
    "Model Name",
    "Firmware Version",
    "Software MCSV",
    "Active Image Partition",
    "Uptime",
  ],
  Usage: [
    "SIM1_Up_1",
    "SIM1_Down_1",
    "SIM1_Tx_1",
    "SIM1_Rx_1",
    "SIM1_Tx(Dropped)_1",
    "SIM1_Rx(Dropped)_1",
    "SIM1_Up_2",
    "SIM1_Down_2",
    "SIM1_Tx_2",
    "SIM1_Rx_2",
    "SIM1_Tx(Dropped)_2",
    "SIM1_Rx(Dropped)_2",
    "SIM2_Up_1",
    "SIM2_Down_1",
    "SIM2_Tx_1",
    "SIM2_Rx_1",
    "SIM2_Tx(Dropped)_1",
    "SIM2_Rx(Dropped)_1",
    "SIM2_Up_2",
    "SIM2_Down_2",
    "SIM2_Tx_2",
    "SIM2_Rx_2",
    "SIM2_Tx(Dropped)_2",
    "SIM2_Rx(Dropped)_2",
  ],
  SIM1: [
    "State",
    "EARFCN",
    "IMSI",
    "ICCID",
    "PLMN",
    "Access",
    "Active",
    "Band",
    "Operator",
    "Phone Number",
    "Roaming",
    "RSSI",
    "RSRP",
    "Signal",
  ],
  SIM2: [
    "State",
    "EARFCN",
    "IMSI",
    "ICCID",
    "PLMN",
    "Access",
    "Active",
    "Band",
    "Operator",
    "Phone Number",
    "Roaming",
    "RSSI",
    "RSRP",
    "Signal",
  ],
  LAN: ["IPv4 Address", "IPv4 Mask", "IPv6 Address", "IPv6 Uptime"],
  LTE: [
    "Active",
    "IPv4 Address(APN)",
    "IPv4 Gateway(APN)",
    "IPv4 Mask(APN)",
    "IPv4 Uptime(APN)",
    "IPv4 Address",
    "IPv4 Gateway",
    "IPv4 Mask",
    "IPv4 Uptime",
  ],
  WAN: ["Active", "IPv4 Address", "IPv4 Gateway", "IPv4 Mask", "IPv4 Uptime"],
  WWAN: [
    "IPv4 Active",
    "IPv4 MAC",
    "IPv4 SSID",
    "IPv4 Address",
    "IPv4 Channel",
    "IPv4 Gateway",
    "IPv4 Mask",
    "IPv4 Uptime",
  ],
  SIM1_connect: [
    "Active#1",
    "IPv4_Address#1",
    "IPv4_Mask#1",
    "IPv4_Gateway#1",
    "IPv4_Uptime#1",
    "Active#2",
    "IPv4_Address#2",
    "IPv4_Mask#2",
    "IPv4_Gateway#2",
    "IPv4_Uptime#2",
  ],
  SIM2_connect: [
    "Active#1",
    "IPv4_Address#1",
    "IPv4_Mask#1",
    "IPv4_Gateway#1",
    "IPv4_Uptime#1",
    "Active#2",
    "IPv4_Address#2",
    "IPv4_Mask#2",
    "IPv4_Gateway#2",
    "IPv4_Uptime#2",
  ],
  DNS: ["IPv4_#1", "IPv4_#2", "IPv4_#3", "IPv6_#1", "IPv6_#2", "IPv6_#3"],
  GPS: ["Latitude", "Longitude", "Horizontal", "Altitude", "Date", "UTC-Time", "Satellite"],
};

const AllOptionKeys = Object.keys(statusOptions) as Array<keyof typeof statusOptions>;

interface checkRowProps {
  rowOptionKey: string; //row的key ex: Identity
  rowOptions: string[]; //row的選項 ex: ['IMEI', 'Bootloader Version', 'Hardware MCSV']
  selectedOptions: typeof statusOptions; //所有row&選到的options ex: {Identity:[],Usage:[...]}
  setSelectedOptions: (arg: typeof statusOptions) => void; //有那些選項(全部row)
}

//選單組件
const CheckRow = ({ rowOptionKey, rowOptions, selectedOptions, setSelectedOptions }: checkRowProps) => {
  const [checkedList, setCheckedList] = useState<string[]>(rowOptions || []); //那些選項要勾選(單一row)
  const [indeterminate, setIndeterminate] = useState(true);
  const [checkAll, setCheckAll] = useState(false); //是否全選

  //選單初始化, 確認localstorage有無設定
  useEffect(() => {
    const columnSetting = localStorage.getItem("allRouterColumn");
    if (columnSetting) {
      let showColumRecord = JSON.parse(columnSetting);
      setCheckedList(showColumRecord?.[rowOptionKey] || []);
      setCheckAll(showColumRecord?.[rowOptionKey]?.length === rowOptions.length);
    }
  }, []);

  const onChange = (list: string[]) => {
    setCheckedList(list);
    setSelectedOptions({ ...selectedOptions, ...{ [rowOptionKey]: list } });
    setIndeterminate(!!list.length && list.length < rowOptions.length);
    setCheckAll(list.length === rowOptions.length);
    localStorage.setItem("allRouterColumn", JSON.stringify({ ...selectedOptions, ...{ [rowOptionKey]: list } }));
  };

  const onCheckAllChange = (e: any) => {
    setCheckedList(e.target.checked ? rowOptions : []);
    setSelectedOptions({ ...selectedOptions, ...{ [rowOptionKey]: e.target.checked ? rowOptions : [] } });
    setIndeterminate(false);
    setCheckAll(e.target.checked);

    localStorage.setItem(
      "allRouterColumn",
      JSON.stringify({ ...selectedOptions, ...{ [rowOptionKey]: e.target.checked ? rowOptions : [] } })
    );
  };

  return (
    <Row gutter={24}>
      <Col xs={10} sm={7} md={5} lg={2}>
        <Checkbox indeterminate={indeterminate} onChange={onCheckAllChange} checked={checkAll}>
          {rowOptionKey}
        </Checkbox>
      </Col>
      <Divider type={"vertical"} style={{ marginBottom: "13px" }} />
      <Col xs={14} sm={17} md={19} lg={19}>
        {/* @ts-ignore */}
        <CheckboxGroup options={rowOptions} value={checkedList} onChange={onChange} />
      </Col>
    </Row>
  );
};
// /overview 內 Status 的 頁面

const RouterStateC = React.memo(() => {
  const { stateCid, isLogin, loginCid, level } = useAppSelector((state) => state.global);

  const { data: status, isFetching: deviceStatusFetching } = useDeviceStatus(level, stateCid, loginCid);
  const deviceStatus = status?.response?.device_status || [];
  const { data: identity, isFetching: deviceIdentityFetching } = useIdentity(level, stateCid, loginCid);
  const deviceIdentity = identity?.response?.device_identity || [];
  const loading = deviceStatusFetching || deviceIdentityFetching;

  const [DeviceStatus, setDeviceStatus] = useState<any[]>([]);

  const [BtnVisible, setBtnVisible] = useState(false);
  const [Restore, setRestore] = useState<any[]>([]);
  const { t } = useTranslation();

  const [selectedOptions, setSelectedOptions] = useState({ ...statusOptions }); //checked box status
  //@ts-ignore
  const [columns, setColumn] = useState<ColumnsType<any>>([...ExtraColumns]);

  useEffect(() => {
    const selectedOptionsKeys = Object.keys(selectedOptions) as Array<keyof typeof selectedOptions>;
    let selectedColumns: any = [];
    selectedOptionsKeys.forEach((item) => {
      //排除children為空陣列
      if (!selectedOptions[item].length) {
        return;
      }

      selectedColumns.push({
        width: 200,
        title: item,
        dataIndex: item,
        fixed: true,
        children: selectedOptions[item].map((sub) => {
          return {
            title: sub,
            dataIndex: `${item}_${sub}`.replaceAll(" ", "_"),
            width: 200,
          };
        }),
      });
    });

    setColumn([...ExtraColumns, ...selectedColumns]);
  }, [selectedOptions]);

  useEffect(() => {
    const columnSetting = localStorage.getItem("allRouterColumn");
    let showColumRecord;
    if (columnSetting) {
      showColumRecord = JSON.parse(columnSetting);
      setSelectedOptions(showColumRecord);
    }
  }, []);

  const content = (
    <div style={{ maxWidth: "1500px" }}>
      {AllOptionKeys.map((key, index) => {
        return (
          <CheckRow
            key={index}
            rowOptionKey={key}
            rowOptions={statusOptions[key]}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
          />
        );
      })}
      <Divider></Divider>
      <Button onClick={() => setBtnVisible(false)}>Close</Button>
    </div>
  );

  useEffect(() => {
    if (loading) return;
    //@ts-ignore
    let tableData: any[] = deviceStatus.reduce((acc, cur) => {
      const identity = deviceIdentity.find((item) => item.nodeInf.id === cur.nodeInf.id)?.obj || {};

      return [...acc, { ...cur.nodeInf, ...cur.obj, ...identity }];
    }, []);

    tableData = tableData.map((item: any) => {
      let partition = item?.identity?.partition;
      let activePartition = partition?.filter((item: any) => item.active);
      const firmwareVersion = activePartition?.[0]?.software_mcsv.slice(8, 11);
      const lteIPv4Uptime = item.status?.connection?.lte
        ? item.status?.connection?.lte.ipv4?.uptime
        : item.status?.connection?.sim1?.[0]?.active
        ? item.status?.connection?.sim1?.[0]?.ipv4?.uptime
        : item.status?.connection?.sim1?.[1]?.active
        ? item.status?.connection?.sim1?.[1]?.ipv4?.uptime
        : item.status?.connection?.sim2?.[0]?.active
        ? item.status?.connection?.sim2?.[0]?.ipv4?.uptime
        : item.status?.connection?.sim2?.[1]?.ipv4?.uptime;
      // const lteIPv4Uptime = item.status?.connection?.sim1?.[0]?.active
      //   ? item.status?.connection?.sim1?.[0]?.ipv4?.uptime
      //   : item.status?.connection?.sim1?.[1]?.active
      //   ? item.status?.connection?.sim1?.[1]?.ipv4?.uptime
      //   : item.status?.connection?.sim2?.[0]?.active
      //   ? item.status?.connection?.sim2?.[0]?.ipv4?.uptime
      //   : item.status?.connection?.sim2?.[1]?.ipv4?.uptime;

      // console.log("Leo debug item", lteIPv4Uptime, item);

      return {
        Device: item.name !== "" ? item.name : item.id,
        Location: item.city,
        Model: item.model,
        Health: item.health,
        Strength: item.sim,
        Group: item.gid,
        Description: item.description,
        Identity_IMEI: item.identity?.IMEI,
        Identity_Bootloader_Version: item.identity?.bootloader_version,
        Identity_Hardware_MCSV: item.identity?.hardware_mcsv,
        Identity_Hostname: item.identity?.hostname,
        Identity_LAN_Ethernet_MAC: item.identity?.lan_eth_mac,
        Identity_Modem_Firmware_Version: item.identity?.modem_firmware_version,
        Identity_Serial_Number: item.identity?.serial_number,
        Identity_WAN_Ethernet_MAC: item.identity?.wan_eth_mac,
        Identity_WiFi_AP_MAC: item.identity?.wifi_ap_mac || item.identity?.["wifi_2.4G_mac"],
        Identity_Model_Name: item.identity?.model_name,
        Identity_Firmware_Version: isNaN(firmwareVersion) ? "" : `V${(firmwareVersion / 100).toFixed(2)}`,
        Identity_Dual_Image_A_MCSV: partition?.[0]?.software_mcsv,
        Identity_Dual_Image_B_MCSV: partition?.[1]?.software_mcsv,
        Identity_Software_MCSV: activePartition?.[0]?.software_mcsv,
        Identity_Active_Image_Partition: activePartition?.[0]?.name,
        Identity_Uptime: Uptime(item.status?.system?.uptime),
        Identity_WiFi_5G_Mac: item.identity?.wifi_5G_mac,
        LAN_IPv4_Address: item.status?.connection?.lan.ipv4.address,
        LAN_IPv4_Mask: item.status?.connection?.lan.ipv4.netmask,
        LAN_IPv6_Address: item.status?.connection?.lan.ipv6.address,
        LAN_IPv6_Uptime: item.status?.connection?.lan.ipv6.uptime,
        LTE_Active: booleanToString(item.status?.connection?.lte?.active),
        "LTE_IPv4_Address(APN)": item.status?.connection?.lte?.apn?.[0].ipv4?.address,
        "LTE_IPv4_Gateway(APN)": item.status?.connection?.lte?.apn?.[0].ipv4?.gateway,
        "LTE_IPv4_Mask(APN)": item.status?.connection?.lte?.apn?.[0].ipv4?.netmask,
        "LTE_IPv4_Uptime(APN)": Uptime(lteIPv4Uptime),
        LTE_IPv4_Address: item.status?.connection?.lte?.ipv4?.address,
        LTE_IPv4_Gateway: item.status?.connection?.lte?.ipv4?.gateway,
        LTE_IPv4_Mask: item.status?.connection?.lte?.ipv4?.netmask,
        LTE_IPv4_Uptime: Uptime(lteIPv4Uptime),
        WAN_Active: booleanToString(item.status?.connection?.wan?.active),
        WAN_IPv4_Address: item.status?.connection?.wan?.ipv4?.address,
        WAN_IPv4_Gateway: item.status?.connection?.wan?.ipv4?.gateway,
        WAN_IPv4_Mask: item.status?.connection?.wan?.ipv4?.netmask,
        WAN_IPv4_Uptime: item.status?.connection?.wan?.ipv4?.uptime,
        WWAN_IPv4_Active: booleanToString(item.status?.connection?.wwan?.active),
        WWAN_IPv4_MAC: item.status?.connection?.wwan?.ipv4?.MAC,
        WWAN_IPv4_SSID: item.status?.connection?.wwan?.ipv4?.SSID,
        WWAN_IPv4_Address: item.status?.connection?.wwan?.ipv4?.address,
        WWAN_IPv4_Channel: item.status?.connection?.wwan?.ipv4?.channel,
        WWAN_IPv4_Gateway: item.status?.connection?.wwan?.ipv4?.gateway,
        WWAN_IPv4_Mask: item.status?.connection?.wwan?.ipv4?.netmask,
        WWAN_IPv4_Uptime: Uptime(item.status?.connection?.wwan?.ipv4?.uptime),
        "SIM1_connect_Active#1": booleanToString(item.status?.connection?.sim1?.[0]?.active),
        "SIM1_connect_IPv4_Address#1": item.status?.connection?.sim1?.[0]?.ipv4?.address,
        "SIM1_connect_IPv4_Mask#1": item.status?.connection?.sim1?.[0]?.ipv4?.netmask,
        "SIM1_connect_IPv4_Gateway#1": item.status?.connection?.sim1?.[0]?.ipv4?.gateway,
        "SIM1_connect_IPv4_Uptime#1":
          item.status?.connection?.sim1?.[0]?.active && Uptime(item.status?.connection?.sim1?.[0]?.ipv4?.uptime),
        "SIM1_connect_Active#2": booleanToString(item.status?.connection?.sim1?.[1]?.active),
        "SIM1_connect_IPv4_Address#2": item.status?.connection?.sim1?.[1]?.ipv4?.address,
        "SIM1_connect_IPv4_Mask#2": item.status?.connection?.sim1?.[1]?.ipv4?.netmask,
        "SIM1_connect_IPv4_Gateway#2": item.status?.connection?.sim1?.[1]?.ipv4?.gateway,
        "SIM1_connect_IPv4_Uptime#2":
          item.status?.connection?.sim1?.[1]?.active && Uptime(item.status?.connection?.sim1?.[1]?.ipv4?.uptime),
        "SIM2_connect_Active#1": booleanToString(item.status?.connection?.sim2?.[0]?.active),
        "SIM2_connect_IPv4_Address#1": item.status?.connection?.sim2?.[0]?.ipv4?.address,
        "SIM2_connect_IPv4_Mask#1": item.status?.connection?.sim2?.[0]?.ipv4?.netmask,
        "SIM2_connect_IPv4_Gateway#1": item.status?.connection?.sim2?.[0]?.ipv4?.gateway,
        "SIM2_connect_IPv4_Uptime#1":
          item.status?.connection?.sim2?.[0]?.active && Uptime(item.status?.connection?.sim2?.[0]?.ipv4?.uptime),
        "SIM2_connect_Active#2": booleanToString(item.status?.connection?.sim2?.[1]?.active),
        "SIM2_connect_IPv4_Address#2": item.status?.connection?.sim2?.[1]?.ipv4?.address,
        "SIM2_connect_IPv4_Mask#2": item.status?.connection?.sim2?.[1]?.ipv4?.netmask,
        "SIM2_connect_IPv4_Gateway#2": item.status?.connection?.sim2?.[1]?.ipv4?.gateway,
        "SIM2_connect_IPv4_Uptime#2":
          item.status?.connection?.sim2?.[1]?.active && Uptime(item.status?.connection?.sim2?.[1]?.ipv4?.uptime),
        "DNS_IPv4_#1": item.status?.dns.wan[0].ipv4[0],
        "DNS_IPv4_#2": item.status?.dns.wan[0].ipv4[1],
        "DNS_IPv4_#3": item.status?.dns.wan[0].ipv4[2],
        "DNS_IPv6_#1": item.status?.dns.wan[0].ipv6[0],
        "DNS_IPv6_#2": item.status?.dns.wan[0].ipv6[1],
        "DNS_IPv6_#3": item.status?.dns.wan[0].ipv6[2],
        Usage_SIM1_Up_1: item.status?.statistic?.lte?.up_kbps || item.status?.statistic?.sim1?.[0]?.up_kbps,
        Usage_SIM1_Down_1: item.status?.statistic?.lte?.down_kbps || item.status?.statistic?.sim1?.[0]?.down_kbps,
        Usage_SIM1_Tx_1: item.status?.statistic?.lte?.tx_kbytes || item.status?.statistic?.sim1?.[0]?.tx_kbytes,
        Usage_SIM1_Rx_1: item.status?.statistic?.lte?.rx_kbytes || item.status?.statistic?.sim1?.[0]?.rx_kbytes,
        Usage_SIM1_Up_2: item.status?.statistic?.sim1?.[1]?.up_kbps,
        Usage_SIM1_Down_2: item.status?.statistic?.sim1?.[1]?.down_kbps,
        Usage_SIM1_Tx_2: item.status?.statistic?.sim1?.[1]?.tx_kbytes,
        Usage_SIM1_Rx_2: item.status?.statistic?.sim1?.[1]?.rx_kbytes,

        Usage_SIM2_Up_1: item.status?.statistic?.sim2?.[0]?.up_kbps,
        Usage_SIM2_Down_1: item.status?.statistic?.sim2?.[0]?.down_kbps,
        Usage_SIM2_Tx_1: item.status?.statistic?.sim2?.[0]?.tx_kbytes,
        Usage_SIM2_Rx_1: item.status?.statistic?.sim2?.[0]?.rx_kbytes,
        Usage_SIM2_Up_2: item.status?.statistic?.sim2?.[1]?.up_kbps,
        Usage_SIM2_Down_2: item.status?.statistic?.sim2?.[1]?.down_kbps,
        Usage_SIM2_Tx_2: item.status?.statistic?.sim2?.[1]?.tx_kbytes,
        Usage_SIM2_Rx_2: item.status?.statistic?.sim2?.[1]?.rx_kbytes,

        "Usage_SIM1_Tx(Dropped)_1":
          item.status?.statistic?.lte?.tx_dropped_pkts || item.status?.statistic?.sim1?.[0]?.tx_dropped_pkts,
        "Usage_SIM1_Rx(Dropped)_1":
          item.status?.statistic?.lte?.rx_dropped_pkts || item.status?.statistic?.sim1?.[0]?.rx_dropped_pkts,
        "Usage_SIM1_Tx(Dropped)_2": item.status?.statistic?.sim1?.[1]?.tx_dropped_pkts,
        "Usage_SIM1_Rx(Dropped)_2": item.status?.statistic?.sim1?.[1]?.rx_dropped_pkts,
        "Usage_SIM2_Tx(Dropped)_1": item.status?.statistic?.sim2?.[0]?.tx_dropped_pkts,
        "Usage_SIM2_Rx(Dropped)_1": item.status?.statistic?.sim2?.[0]?.rx_dropped_pkts,
        "Usage_SIM2_Tx(Dropped)_2": item.status?.statistic?.sim2?.[1]?.tx_dropped_pkts,
        "Usage_SIM2_Rx(Dropped)_2": item.status?.statistic?.sim2?.[1]?.rx_dropped_pkts,
        SIM1_State: item.status?.sim?.[0]?.status,
        SIM1_EARFCN: item.status?.sim?.[0]?.EARFCN,
        SIM1_IMSI: item.status?.sim?.[0]?.IMSI,
        SIM1_ICCID: item.status?.sim?.[0]?.ICCID,
        SIM1_PLMN: item.status?.sim?.[0]?.PLMN,
        SIM1_Access: item.status?.sim?.[0]?.access,
        SIM1_Active: booleanToString(item.status?.sim?.[0]?.active),
        SIM1_Band: item.status?.sim?.[0]?.band,
        SIM1_Operator: item.status?.sim?.[0]?.operator,
        SIM1_Phone_Number: item.status?.sim?.[0]?.phone_number,
        SIM1_Roaming: booleanToString(item.status?.sim?.[0]?.roaming),
        SIM1_RSSI: item.status?.sim?.[0]?.rssi_dbm,
        SIM1_RSRP: item.status?.sim?.[0]?.rsrp_dbm,
        SIM1_Signal: item.status?.sim?.[0]?.signal,
        SIM2_State: item.status?.sim?.[1]?.status || "",
        SIM2_EARFCN: item.status?.sim?.[1]?.EARFCN || "",
        SIM2_IMSI: item.status?.sim?.[1]?.IMSI || "",
        SIM2_ICCID: item.status?.sim?.[1]?.ICCID || "",
        SIM2_PLMN: item.status?.sim?.[1]?.PLMN || "",
        SIM2_Access: item.status?.sim?.[1]?.access || "",
        SIM2_Active: booleanToString(item.status?.sim?.[1]?.active),
        SIM2_Band: item.status?.sim?.[1]?.band || "",
        SIM2_Operator: item.status?.sim?.[1]?.operator || "",
        SIM2_Phone_Number: item.status?.sim?.[1]?.phone_number || "",
        SIM2_Roaming: booleanToString(item.status?.sim?.[1]?.roaming),
        SIM2_RSSI: item.status?.sim?.[1]?.rssi_dbm,
        SIM2_RSRP: item.status?.sim?.[1]?.rsrp_dbm,
        SIM2_Signal: item.status?.sim?.[1]?.signal,
        GPS_Latitude: item.status?.gps?.latitude,
        GPS_Longitude: item.status?.gps?.longitude,
        GPS_Horizontal: item.status?.gps?.horizontal,
        GPS_Altitude: item.status?.gps?.altitude,
        GPS_Date: item.status?.gps?.date,
        "GPS_UTC-Time": item.status?.gps?.["utc-time"],
        GPS_Satellite: item.status?.gps?.satellite,
      };
    });

    setDeviceStatus(tableData);
    setRestore(tableData);
  }, [loading]);

  let pagstorageData = localStorage.getItem("setting.page");

  let paginationSetting: any;
  if (pagstorageData) {
    paginationSetting = JSON.parse(pagstorageData);
  }

  const booleanToString = (arg: boolean) => {
    if (arg === true) {
      return "Enable";
    } else if (arg === false) {
      return "Disable";
    } else {
      return "";
    }
  };

  const [defaultPageSize, setDefaultPageSize] = useState(paginationSetting?.routerPageSize || 20);

  function OnPageChange(_: any, size: any) {
    setDefaultPageSize(size);
    if (paginationSetting?.devicePageSize) {
      paginationSetting.routerPageSize = size;
    } else {
      paginationSetting = { routerPageSize: size };
    }
    localStorage.setItem("setting.page", JSON.stringify(paginationSetting));
  }

  return (
    <Fragment>
      <RouterStateFilterC setDeviceStatus={setDeviceStatus} Restore={Restore} uploading={loading} />
      {/* <BackTop visibilityHeight={100} /> */}
      <Card
        title={
          <div className="flex items-center">
            {t("ISMS.Status")}
            <Button className="ml-1" size="small" onClick={() => exportToExcel(DeviceStatus)}>
              Export
            </Button>
          </div>
        }
        extra={
          <Space size={1}>
            <Refresh queriesArray={["status", "identity"]} />

            <AutoRefresh queriesArray={["status", "identity"]} />

            <Tooltip title={t("ISMS.Setting")}>
              <Popover
                placement="left"
                trigger="click"
                onVisibleChange={(visible) => setBtnVisible(visible)}
                content={content}
                visible={BtnVisible}
                // style={{ overflowY: "auto" }}
              >
                <Button icon={<SettingOutlined />}></Button>
              </Popover>
            </Tooltip>
          </Space>
        }
      >
        <Table
          sticky
          loading={loading}
          pagination={{
            onShowSizeChange: (_, size) => OnPageChange(_, size),
            defaultPageSize: defaultPageSize,
          }}
          bordered
          dataSource={DeviceStatus}
          columns={columns}
          // scroll={{ x: "calc(700px + 50%)", y: 500 }}
          scroll={{ x: "calc(700px + 50%)" }}
          className={styles.allstatusTable}
          rowKey={(record: any) => record.Device}
        />
      </Card>
    </Fragment>
  );
});

export default RouterStateC;
